import { fetchLayoutResult as fetchLayoutResultDebug } from './layoutDebugAPI'
import { fetchArticles } from '../article/articleAPI'
import { fetchLayoutResult } from '../page/pageAPI'
import { snakeCase } from 'lodash'

// ================================================================================
// jsonから得られたデータをAppのreact redux state(App本来のAPIレスポンス）
// に適合させるための変換関数群
// ================================================================================

// IDは数値でなければならないが過去のサンプルに文字列のものがある
// そのため数値文字列に変換する
const numberHash = (str: string) => [...str].reduce((a, c) => ((a * 33) ^ c.charCodeAt(0)) >>> 0, 5381)

const passIfNumberOrHash = (str: string | undefined, prefix: string) => {
  if (str === undefined) {
    return undefined
  }
  const idBody = str.replace(new RegExp(`^${prefix}-`), '')
  const idNumber = Number(idBody)
  return Number.isNaN(idNumber) ? numberHash(idBody) : idNumber
}

// エクスポートされたinputのidにはプレフィクスがついているため除去した上でハッシュする
const normalizeInputId = (str: string | undefined) => {
  return passIfNumberOrHash(str, 'article')
}

// inputのheading idのハッシュ化、その他正規化
const normalizeInputHeadings = (headings: any[]) => {
  return (headings ?? []).map((heading) => ({
    ...heading,
    id: passIfNumberOrHash(heading.id, 'heading'),
    headingType: snakeCase(heading.type)
  }))
}

// inputのfigure idのハッシュ化
const normalizeInputFigures = (figures: any[]) => {
  return (figures ?? []).map((figure) => ({
    ...figure,
    id: passIfNumberOrHash(figure.id, 'figure')
  }))
}

// outputのidの正規化、データソースによりプレフィクスの有無が揺れるので確実にする
const normalizeLayoutResultId = (str: string | undefined) => {
  const idStr = passIfNumberOrHash(str, 'article')
  return `article-${idStr}`
}

// outputのheading idの正規化
const normalizeLayoutResultHeadingId = (id: string | undefined) => {
  const idStr = passIfNumberOrHash(id, 'heading')
  return `heading-${idStr}`
}

const normalizeLayoutResultHeadingAreas = (headings: any[]) => {
  return (headings ?? []).map((heading) => {
    const id = heading.id ? normalizeLayoutResultHeadingId(heading.id) : undefined
    return {
      ...heading,
      id
    }
  })
}

// outputのfigure idの正規化
const normalizeLayoutResultFigureId = (id: string | undefined) => {
  const idStr = passIfNumberOrHash(id, 'figure')
  return `figure-${idStr}`
}

const normalizeLayoutResultFigures = (figures: any[]) => {
  return (figures ?? []).map((figure) => {
    const id = figure.id ? normalizeLayoutResultFigureId(figure.id) : undefined
    return {
      ...figure,
      id
    }
  })
}

// layout resultの整形
const normalizeLayoutResults = (outputData: any[]) => {
  return outputData.map((data) => ({
    ...data,
    articles: data.articles.map((article: any) => ({
      ...article,
      id: normalizeLayoutResultId(article.id),
      headingAreas: normalizeLayoutResultHeadingAreas(article.headingAreas),
      figures: normalizeLayoutResultFigures(article.figures)
    }))
  }))
}

// debug apiのペイロードをappのarticle fetchArticlesのそれに変換する
const fromInputPayloadToDummyArticlePayload = (dataPrefix: string, inputData: any, layoutResults: any[]) => {
  const { articles, ...layoutable } = inputData
  const list = articles.map((article: any) => ({
    ...article,
    id: normalizeInputId(article.id),
    articleType: article.type,
    preambleContent: article.preambleContent ?? null,
    headings: normalizeInputHeadings(article.headings),
    figures: normalizeInputFigures(article.figures),
    areas: (article.areas ?? []).map((area: any) =>
      Object.fromEntries(Object.entries(area).map(([k, v]) => [k, String(v)]))
    )
  }))
  return {
    layoutable: {
      ...layoutable,
      name: dataPrefix,
      layoutIndex: 0,
      layoutResult: layoutResults
    },
    layoutableType: 'Page',
    list
  }
}

// debug apiのペイロードをappのpage fetchLayoutResultのそれに変換する
const fromOutputPayloadToDummyLayoutResultPayload = (_dataPrefix: string, _inputData: any, layoutResults: any[]) => {
  return {
    layoutResults,
    layoutIndex: 0,
    lastEngineStatusLog: null
  }
}

// ================================================================================
// デバッグプレビュー用のミドルウェア
// デバッグプレビュー用APIからのレスポンスのreducerをApp本来の方に振り返る
// ================================================================================

export const layoutDebugMiddleware = (_store: any) => (next: any) => (action: any) => {
  if (action.type === fetchLayoutResultDebug.fulfilled.type) {
    const layoutResults = normalizeLayoutResults(action.payload.output)

    const articlesDummyPayload = fromInputPayloadToDummyArticlePayload(
      action.payload.dataName,
      action.payload.input,
      layoutResults
    )
    const layoutResultDummyPayload = fromOutputPayloadToDummyLayoutResultPayload(
      action.payload.dataName,
      action.payload.input,
      layoutResults
    )

    next({
      type: fetchArticles.fulfilled.type,
      payload: articlesDummyPayload
    })
    next({
      type: fetchLayoutResult.fulfilled.type,
      payload: layoutResultDummyPayload
    })
  }
  return next(action)
}
